import * as React from 'react'
import styled from 'styled-components'
import { TextArea } from '../../../../common/src/components/core'
import { Fonts } from '../../../../common/src/constants'

const DEFAULT_MIN_HEIGHT = 98

interface TextAreaAutosizeProps {
  innerRef?: React.Ref<HTMLTextAreaElement>
  value: string
  onChange: (val: string) => void
  onClick?: () => void
  placeholder?: string
  disabled?: boolean
  minHeight?: number
  id?: string
  hasError?: boolean
}

export default class TextAreaAutosize extends React.PureComponent<
  TextAreaAutosizeProps,
  {
    height: number
  }
> {
  private ghost?: HTMLElement | null
  private unmounting: boolean
  private element?: HTMLElement | null

  constructor(props: TextAreaAutosizeProps) {
    super(props)

    this.state = {
      height: this.getMinHeight(),
    }

    this.unmounting = false
  }

  componentDidMount() {
    window.requestAnimationFrame(this.frame)
  }

  componentWillUnmount() {
    this.unmounting = true
  }

  render() {
    return (
      <TextAreaAutosizeWrap onClick={this.props.onClick}>
        {this.getExpandableField()}
        {this.getGhostField()}
      </TextAreaAutosizeWrap>
    )
  }

  private frame = () => {
    if (this.unmounting) {
      return
    }

    if (this.ghost != null) {
      const ghostHeight = this.ghost.clientHeight
      const scrollHeight = (this.element && this.element.scrollHeight) || 0

      const height = Math.max(this.getMinHeight(), ghostHeight, scrollHeight)

      if (height !== this.state.height) {
        this.setState({ height })
      }
    }

    window.requestAnimationFrame(this.frame)
  }

  private setValue = (event: { target: { value: string } }) => {
    this.props.onChange(event.target.value)
  }

  private getExpandableField() {
    const { value, placeholder } = this.props

    return (
      <TextAreaExpandable
        ref={this.props.innerRef}
        id={this.props.id}
        height={this.state.height}
        value={value}
        placeholder={placeholder}
        onChange={this.setValue}
        onInput={this.frame}
      />
    )
  }

  private getGhostField() {
    return (
      <GhostDiv ref={this.setGhostElem} aria-hidden="true">
        &#8203;
        {this.props.value}
        &#8203;
      </GhostDiv>
    )
  }

  private setGhostElem = (e: HTMLDivElement) => {
    this.ghost = e
  }

  private getMinHeight() {
    return this.props.minHeight || DEFAULT_MIN_HEIGHT
  }
}

const TextAreaAutosizeWrap = styled.div`
  position: relative;
`

const TextAreaExpandable = styled(TextArea)`
  width: 100%;
  height: ${(props: { height: number }) => props.height + 'px'};
`

const GhostDiv = styled.div`
  padding: 13px;
  font-family: ${Fonts.sansBody};
  font-size: 16px;
  line-height: 1.5;
  white-space: pre-wrap;
  visibility: hidden;
  position: absolute;
  top: 0;
`
